import React from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import SalaryConfigTable from "@/components/common/SalaryConfigTable";
import CommonFilter from "@/components/common/CommonFilter";
import TablePagination from "@/components/common/TablePagination";
import { Button } from "@/components/ui/button";
import type { IUser } from "@/interface/employeeOnBoard";
import { API_URL } from "@/api/constant";
import { useBranchSalaryConfig } from "@/hooks/useBranchSalaryConfig";
import {
    Download,
    Upload,
    Building2,
    CheckCircle2,
    FileCheck,
    X,
    Users,
    Check,
    Coins,
} from "lucide-react";

/**
 * BranchSalaryConfig Page Component
 * Refactored using senior React standards:
 * - Complex state management & API flow delegated to `useBranchSalaryConfig` custom hook.
 * - Clean UI layout with zero breaking changes to existing features or visual design.
 */
const BranchSalaryConfig: React.FC = () => {
    const {
        viewMode,
        setViewMode,
        branches,
        branchesWiseSalaryHead,
        selectedBranchId,
        isLoadingBranches,
        employees,
        isLoadingEmployees,
        filter,
        paginationData,
        setPaginationData,
        backupUsers,
        isLoadingBackups,
        backupPagination,
        setBackupPagination,
        selectedEmployeeIds,
        isAllBranchUsersSelected,
        selectedFile,
        isDownloading,
        isUploading,
        fileInputRef,
        canDownloadTemplate,
        activeBranchName,

        handleBranchSelect,
        handleSearchChange,
        handleChangePageSize,
        handleToggleSelectAllBranchUsers,
        handleSelectAll,
        handleSelectItem,
        handleClearSelection,
        handleDownloadTemplate,
        handleFileChange,
        handleClearFile,
        handleUploadFile,
        handleSaveUserSalary,
    } = useBranchSalaryConfig();

    // ── Table Columns for Regular Employee View Mode ─────────
    const employeeColumns: TableColumn<IUser>[] = [
        {
            key: "sNo",
            header: "S.No",
            render: (_item: IUser, index: number) => (
                <span className="text-muted-foreground font-medium text-xs">
                    {String((paginationData.currentPage - 1) * paginationData.pageSize + index + 1).padStart(2, "0")}
                </span>
            ),
        },
        {
            key: "name",
            header: "Employee",
            render: (item: IUser) => (
                <div className="flex items-center gap-3">
                    {item?.profilePhoto ? (
                        <img
                            src={`${API_URL}/${item.profilePhoto}`}
                            alt={item.name}
                            className="h-9 w-9 min-w-9 rounded-full object-cover shadow-xs"
                        />
                    ) : (
                        <div className="h-9 w-9 min-w-9 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center shadow-xs">
                            {item?.name?.charAt(0)?.toUpperCase() || "E"}
                        </div>
                    )}
                    <div>
                        <div className="font-semibold text-slate-800 text-sm">{item?.name || "N/A"}</div>
                        <div className="text-xs text-slate-500">{item?.email || "No email"}</div>
                    </div>
                </div>
            ),
        },
        {
            key: "phone",
            header: "Phone",
            render: (item: IUser) => (
                <span className="font-medium text-slate-700 text-xs">{item?.phone || "—"}</span>
            ),
        },
        {
            key: "department",
            header: "Department",
            render: (item: IUser) => (
                <span className="text-slate-700 text-xs font-medium">{item?.department?.name || "—"}</span>
            ),
        },
        {
            key: "designation",
            header: "Designation",
            render: (item: IUser) => (
                <span className="text-slate-600 text-xs">{item?.designation?.name || "—"}</span>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (item: IUser) => {
                const isCurrent = item?.status?.toLowerCase() === "current";
                return (
                    <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${isCurrent
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                            }`}
                    >
                        <span
                            className={`h-1.5 w-1.5 rounded-full ${isCurrent ? "bg-emerald-500" : "bg-rose-500"}`}
                        />
                        {item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "Unknown"}
                    </span>
                );
            },
        },
    ];

    // ── Additional Filters (Action Buttons in CommonFilter) ─
    const additionalFilters = (
        <>
            {viewMode !== "salary_backups" && (
                <div className="flex flex-wrap items-center gap-2">
                    {/* Hidden File Input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx, .xls, .csv"
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* 1. Select All Branch Users Button */}
                    <Button
                        type="button"
                        variant={isAllBranchUsersSelected ? "default" : "outline"}
                        size="sm"
                        onClick={handleToggleSelectAllBranchUsers}
                        className={`gap-2 font-semibold h-9 rounded-xl transition-all ${isAllBranchUsersSelected
                            ? "bg-primary text-white shadow-xs"
                            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                            }`}
                    >
                        {isAllBranchUsersSelected ? <Check className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                        {isAllBranchUsersSelected ? "All Branch Users Selected" : "Select All Branch Users"}
                    </Button>

                    {/* 2. Download Template Button */}
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleDownloadTemplate}
                        disabled={!canDownloadTemplate || isDownloading}
                        className={`gap-2 font-semibold h-9 rounded-xl transition-all shadow-2xs ${canDownloadTemplate
                            ? "bg-white hover:bg-blue-50 text-blue-700 border-blue-200"
                            : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            }`}
                    >
                        <Download className="w-4 h-4" />
                        {isDownloading
                            ? "Downloading..."
                            : selectedEmployeeIds.size > 0 && !isAllBranchUsersSelected
                                ? "Selected Employee Download Template"
                                : "Bulk Employee Download Template"}
                    </Button>

                    {/* 3. Upload Excel File Button */}
                    {selectedFile ? (
                        <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-xl px-2.5 py-1">
                            <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1 max-w-[180px] truncate">
                                <FileCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                {selectedFile.name}
                            </span>
                            <button
                                type="button"
                                onClick={handleClearFile}
                                className="text-slate-400 hover:text-slate-600 p-0.5"
                                title="Remove file"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleUploadFile}
                                disabled={isUploading}
                                className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-lg gap-1.5 ml-1"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                {isUploading ? "Uploading..." : "Upload"}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="gap-2 font-semibold h-9 rounded-xl transition-all shadow-2xs bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600"
                            title="Upload Excel File"
                        >
                            <Upload className="w-4 h-4" />
                            Upload Excel File
                        </Button>
                    )}
                </div>
            )}
        </>
    );

    return (
        <Layout>
            <div className="space-y-6">
                {/* Page Title & Selection Counter Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <MainHeading title="Branch Salary Backups Config" />
                    {(selectedEmployeeIds.size > 0 || isAllBranchUsersSelected) && (
                        <div className="flex items-center gap-2 bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 rounded-xl text-xs font-semibold">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>
                                {isAllBranchUsersSelected
                                    ? `All Users Selected for ${activeBranchName}`
                                    : `${selectedEmployeeIds.size} Employee(s) Selected`}
                            </span>
                            <button
                                type="button"
                                onClick={handleClearSelection}
                                className="ml-1 hover:bg-primary/20 p-0.5 rounded-md transition-colors"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Branch Selection Pills */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-xs space-y-3">
                    <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                        <Building2 className="w-4 h-4 text-primary" />
                        <span>Select Branch:</span>
                    </div>

                    {isLoadingBranches ? (
                        <div className="flex gap-2 animate-pulse">
                            <div className="h-9 w-24 bg-slate-200 rounded-xl" />
                            <div className="h-9 w-28 bg-slate-200 rounded-xl" />
                            <div className="h-9 w-24 bg-slate-200 rounded-xl" />
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2.5">
                            {branches?.map((b) => {
                                const isSelected = b.id === selectedBranchId;
                                return (
                                    <Button
                                        key={b.id}
                                        type="button"
                                        variant={isSelected ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handleBranchSelect(b.id ?? "")}
                                        className={`rounded-xl transition-all duration-200 font-semibold px-4 ${isSelected
                                            ? "shadow-sm"
                                            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                            }`}
                                    >
                                        {b.name}
                                    </Button>
                                );
                            })}
                            {branches.length === 0 && (
                                <span className="text-xs text-slate-400">No branches available</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Filter and Table Container */}
                <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3 justify-between flex-1 flex-wrap">
                            <div className="flex gap-3 items-center">
                                <Users className="w-5 h-5 text-slate-700" />
                                <h3 className="text-base font-bold text-slate-800">
                                    {viewMode === "salary_backups" ? "Branch Salary Backups" : "Employees"} (
                                    {viewMode === "salary_backups" ? backupPagination.total : paginationData.total})
                                </h3>
                            </div>

                            {/* View Mode Toggle Buttons */}
                            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={viewMode === "all_employees" ? "default" : "ghost"}
                                    onClick={() => setViewMode("all_employees")}
                                    className="h-7 px-3 text-xs font-semibold rounded-lg gap-1.5"
                                >
                                    <Users className="w-3.5 h-3.5" />
                                    Employee List
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={viewMode === "salary_backups" ? "default" : "ghost"}
                                    onClick={() => setViewMode("salary_backups")}
                                    className="h-7 px-3 text-xs font-semibold rounded-lg gap-1.5"
                                >
                                    <Coins className="w-3.5 h-3.5" />
                                    Show Salary Backups
                                </Button>
                            </div>
                        </div>

                        {(selectedEmployeeIds.size > 0 || isAllBranchUsersSelected) && (
                            <div className="text-xs text-slate-500">
                                {isAllBranchUsersSelected ? (
                                    <span className="font-bold text-primary">All Branch Users Selected</span>
                                ) : (
                                    <span>
                                        <strong className="text-slate-800">{selectedEmployeeIds.size}</strong> employee(s) selected in table
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <CommonFilter
                        searchValue={filter.search || ""}
                        onSearchChange={handleSearchChange}
                        additionalFilters={additionalFilters}
                    />

                    {viewMode === "salary_backups" ? (
                        <SalaryConfigTable
                            data={backupUsers}
                            salaryHead={branchesWiseSalaryHead}
                            loading={isLoadingBackups}
                            stickyColumns={5}
                            selectedItems={selectedEmployeeIds}
                            currentPage={backupPagination.currentPage}
                            pageSize={backupPagination.pageSize}
                            emptyMessage={`No employee salary backups found for ${activeBranchName}`}
                            onSaveUserSalary={handleSaveUserSalary}
                        />
                    ) : (
                        <CommonTable
                            data={employees}
                            columns={employeeColumns}
                            loading={isLoadingEmployees}
                            showCheckbox={true}
                            selectedItems={selectedEmployeeIds}
                            getItemId={(item: IUser) => String(item.id)}
                            stickyColumns={4}
                            onSelectAll={handleSelectAll}
                            onSelectItem={(item: IUser, checked: boolean) => handleSelectItem(String(item?.id ?? ""), checked)}
                            emptyMessage={`No employees found for ${activeBranchName}`}
                        />
                    )}

                    <TablePagination
                        paginationData={viewMode === "salary_backups" ? backupPagination : paginationData}
                        setPaginationData={viewMode === "salary_backups" ? setBackupPagination : setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={viewMode === "salary_backups" ? isLoadingBackups : isLoadingEmployees}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default BranchSalaryConfig;